import React, { useCallback, useEffect, useRef, useState } from "react";
import Sidebar from "../../commons/Sidebar/Sidebar";
import Header from "../../commons/Header/Header";
import { useLocation } from "react-router-dom";
import Download from "../../assets/images/icons/download-icon.svg";
import DateRangeSelectButton from "../../commons/DateRangeSelectButton/DateRangeSelectButton";
import TablePagination from "../../commons/Pagination/TablePagination";
import { FaSearch } from "react-icons/fa";
import { toast } from "react-toastify";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { UI_CONSTANTS } from "../../utils/constants";
import {
  ADMIN_CSV_READY_WINDOW_EVENT,
  REPORT_TYPES,
  getReportDownloadUrl,
  getReportTypeFromPayload,
} from "../../utils/adminCsvSocket";

const TABS = [
  {
    key: "tab1",
    label: "Restaurant Sales Report",
    reportType: REPORT_TYPES.RESTAURANT_SALES,
    createEndpoint: "createRestaurantSalesReport",
  },
  {
    key: "tab2",
    label: "Restaurant Order Report",
    reportType: REPORT_TYPES.RESTAURANT_ORDER,
    createEndpoint: "createRestaurantOrderReport",
  },
  {
    key: "tab3",
    label: "Restaurant AI Call Report",
    reportType: null,
    createEndpoint: null,
  },
  {
    key: "tab4",
    label: "Profit and Lose Report",
    reportType: null,
    createEndpoint: null,
  },
  {
    key: "tab5",
    label: "Payout Report",
    reportType: null,
    createEndpoint: null,
  },
];

const REPORT_STATUS = {
  PENDING: 0,
  READY: 1,
  FAILED: 2,
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getRestaurantLabel = (item) =>
  item?.restaurant_id?.restaurant_name ||
  item?.restaurant_name ||
  item?.name ||
  item?.full_name ||
  item?.email ||
  "Restaurant";

const getRestaurantId = (item) => {
  if (!item) return null;
  if (typeof item.restaurant_id === "string") return item.restaurant_id;
  return item?.restaurant_id?._id || item?._id || item?.id || null;
};

const getStatusMeta = (status) => {
  const value = Number(status);
  if (value === REPORT_STATUS.READY) {
    return { label: "Ready", className: "badge badge-success" };
  }
  if (value === REPORT_STATUS.FAILED) {
    return { label: "Failed", className: "badge badge-danger" };
  }
  return { label: "Pending", className: "badge badge-warning" };
};

const RestaurantSelectDropdown = ({
  restaurants,
  selectedId,
  onChange,
  loading,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  const selectedRestaurant = restaurants.find(
    (item) => String(getRestaurantId(item)) === String(selectedId)
  );

  const filteredRestaurants = restaurants.filter((restaurant) =>
    getRestaurantLabel(restaurant).toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleClear = () => {
    onChange(null);
    setSearchTerm("");
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className="dropdown position-relative d-inline-block">
      <button
        type="button"
        className="btn btn-primary px-4 rounded-2"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {selectedRestaurant
          ? getRestaurantLabel(selectedRestaurant)
          : loading
            ? "Loading..."
            : "Select Restaurant"}{" "}
        <span className="ml-2">&#9662;</span>
      </button>

      {isOpen && (
        <div
          className="dropdown-menu show rounded-14 border-0 shadow-lg p-3"
          style={{
            display: "block",
            left: 0,
            top: "calc(100% + 8px)",
            minWidth: "280px",
            backgroundColor: "#363637",
          }}
        >
          <div className="mb-2 d-flex align-items-center">
            <div
              className="input-group input-group-sm d-flex align-items-center flex-grow-1 px-2"
              style={{
                border: "1px solid rgba(255, 255, 255, 0.35)",
                borderRadius: "8px",
                backgroundColor: "rgba(0, 0, 0, 0.2)",
              }}
            >
              <span className="input-group-text bg-transparent border-0 p-0 pr-2">
                <FaSearch className="text-white" />
              </span>
              <input
                type="text"
                className="form-control border-0 bg-transparent py-2 placeholder-white"
                placeholder="Search Restaurant"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn btn-link text-info ml-2 p-0"
              onClick={handleClear}
            >
              Clear
            </button>
          </div>

          {selectedRestaurant && (
            <div
              className="mb-2 px-2 py-2 small text-white"
              style={{
                border: "1px solid rgba(255, 255, 255, 0.2)",
                borderRadius: "8px",
                backgroundColor: "rgba(255, 255, 255, 0.08)",
              }}
            >
              <span className="text-muted d-block mb-1">Selected</span>
              {getRestaurantLabel(selectedRestaurant)}
            </div>
          )}

          <div
            style={{
              maxHeight: "200px",
              overflowY: "auto",
              borderTop: "1px solid rgba(255, 255, 255, 0.15)",
            }}
          >
            {loading ? (
              <div className="text-muted text-center py-2 small">Loading...</div>
            ) : filteredRestaurants.length > 0 ? (
              filteredRestaurants.map((restaurant) => {
                const id = getRestaurantId(restaurant);
                const isSelected = String(selectedId) === String(id);
                return (
                  <button
                    type="button"
                    key={id}
                    className={`filter-options-menu__item w-100 text-left ${
                      isSelected ? "filter-options-menu__item--selected" : ""
                    }`}
                    style={{
                      borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
                    }}
                    onClick={() => {
                      onChange(id);
                      setSearchTerm("");
                      setIsOpen(false);
                    }}
                  >
                    {getRestaurantLabel(restaurant)}
                  </button>
                );
              })
            ) : (
              <div className="text-muted text-center py-2 small">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

function SalesAndPerformanceReport() {
  const location = useLocation();
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [cms, setCms] = useState({ type: "tab1" });
  const [historyRows, setHistoryRows] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [restaurants, setRestaurants] = useState([]);
  const [loadingRestaurants, setLoadingRestaurants] = useState(false);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const activeTab = TABS.find((tab) => tab.key === cms.type) || TABS[0];
  const canExport = Boolean(activeTab.createEndpoint && activeTab.reportType);

  useEffect(() => {
    if (location?.state) {
      setCms({ type: location.state });
    }
  }, [location.state]);

  const resolveRestaurantName = useCallback(
    (restaurantId) => {
      if (!restaurantId) return "-";
      const match = restaurants.find(
        (item) => String(getRestaurantId(item)) === String(restaurantId)
      );
      return match ? getRestaurantLabel(match) : "-";
    },
    [restaurants]
  );

  const fetchRestaurants = useCallback(async () => {
    setLoadingRestaurants(true);
    try {
      const response = await handleApiCall("get", endpoints.restaurantListDropdown);
      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        const restaurantData = response?.data?.data;
        setRestaurants(
          Array.isArray(restaurantData) ? restaurantData : restaurantData?.data || []
        );
      } else {
        setRestaurants([]);
      }
    } catch (error) {
      console.error("Error fetching restaurants:", error);
      setRestaurants([]);
    } finally {
      setLoadingRestaurants(false);
    }
  }, []);

  const fetchReportHistory = useCallback(async () => {
    if (!activeTab.reportType) {
      setHistoryRows([]);
      setTotalPages(1);
      return;
    }

    setLoadingHistory(true);
    try {
      const params = new URLSearchParams({
        report_type: activeTab.reportType,
        page: String(page),
        limit: String(limit),
      });

      const response = await handleApiCall(
        "get",
        `${endpoints.getXlsxLog}?${params.toString()}`
      );

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        const payload = response?.data?.data;
        const root = response?.data;

        const list = Array.isArray(payload)
          ? payload
          : payload?.data ||
            payload?.list ||
            payload?.logs ||
            payload?.xlsx_logs ||
            [];

        const pagination = payload?.pagination || root?.pagination || {};

        setHistoryRows(Array.isArray(list) ? list : []);
        setTotalPages(
          Number(
            pagination?.totalPages ||
              pagination?.total_pages ||
              payload?.totalPages ||
              1
          ) || 1
        );
      } else {
        setHistoryRows([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching report history:", error);
      setHistoryRows([]);
      setTotalPages(1);
    } finally {
      setLoadingHistory(false);
    }
  }, [activeTab.reportType, page, limit]);

  useEffect(() => {
    fetchRestaurants();
  }, [fetchRestaurants]);

  useEffect(() => {
    fetchReportHistory();
  }, [fetchReportHistory]);

  useEffect(() => {
    const handleCsvReady = (event) => {
      const reportType = getReportTypeFromPayload(event.detail || {});
      if (reportType && reportType === activeTab.reportType) {
        fetchReportHistory();
      }
    };

    window.addEventListener(ADMIN_CSV_READY_WINDOW_EVENT, handleCsvReady);
    return () => window.removeEventListener(ADMIN_CSV_READY_WINDOW_EVENT, handleCsvReady);
  }, [activeTab.reportType, fetchReportHistory]);

  const handleExport = async () => {
    if (!canExport) {
      toast.info("Export for this report will be available soon.");
      return;
    }

    setExporting(true);
    try {
      const params = new URLSearchParams();

      if (selectedRestaurantId) {
        params.set("restaurant_id", String(selectedRestaurantId));
      }
      if (startDate) {
        params.set("start_date", startDate);
      }
      if (endDate) {
        params.set("end_date", endDate);
      }

      const endpoint = endpoints[activeTab.createEndpoint];
      const url = params.toString() ? `${endpoint}?${params.toString()}` : endpoint;
      const response = await handleApiCall("get", url);

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        toast.success(
          response?.data?.message ||
            "Report generation started. You will get a notification when it is ready."
        );
        fetchReportHistory();
      } else {
        toast.error(response?.data?.message || "Failed to start report export.");
      }
    } catch (error) {
      console.error("Error exporting report:", error);
      toast.error("Failed to start report export.");
    } finally {
      setExporting(false);
    }
  };

  const mapHistoryRow = (item, index) => {
    const status = Number(item?.status);
    const downloadUrl = getReportDownloadUrl(item);
    const isReady = status === REPORT_STATUS.READY && Boolean(downloadUrl);
    const restaurantId =
      item?.filter?.restaurant_id ||
      (typeof item?.restaurant_id === "string" ? item.restaurant_id : item?.restaurant_id?._id);

    return {
      id: item?._id || `${page}-${index}`,
      serial: (page - 1) * limit + index + 1,
      name: item?.file_name || item?.report_name || activeTab.label,
      restaurantName: resolveRestaurantName(restaurantId),
      from: formatDate(
        item?.start_date || item?.from_date || item?.fromdate || item?.filter?.start_date
      ),
      to: formatDate(item?.end_date || item?.to_date || item?.todate || item?.filter?.end_date),
      generatedOn: formatDate(item?.createdAt || item?.created_at || item?.generated_on),
      status,
      statusMeta: getStatusMeta(status),
      downloadUrl,
      isReady,
    };
  };

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar status={true} />

        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-4">
              <h3 className="text-default weight-400">
                Sales And Performance Report
              </h3>
            </div>

            <div className="card-heading d-flex align-items-center mb-3 pb-2">
              <div className="d-flex align-items-center col-md-12 px-0 flex-wrap">
                {canExport ? (
                  <RestaurantSelectDropdown
                    restaurants={restaurants}
                    selectedId={selectedRestaurantId}
                    onChange={setSelectedRestaurantId}
                    loading={loadingRestaurants}
                  />
                ) : null}

                <div className="d-flex align-items-center ml-auto mt-2 mt-md-0 flex-nowrap">
                  {canExport ? (
                    <div className="mr-3">
                      <DateRangeSelectButton
                        appliedFrom={startDate}
                        appliedTo={endDate}
                        onApply={(start, end) => {
                          setStartDate(start);
                          setEndDate(end);
                        }}
                        onReset={() => {
                          setStartDate(null);
                          setEndDate(null);
                        }}
                      />
                    </div>
                  ) : null}
                  <button
                    type="button"
                    className="btn btn-primary px-4 rounded-2"
                    onClick={handleExport}
                    disabled={exporting || !canExport}
                  >
                    {exporting ? "Exporting..." : "Export as CSV"}
                  </button>
                </div>
              </div>
            </div>

            <div className="row mx-0 main-content-body border-bottom pb-4">
              <div className="col-md-12 mb-26 px-0 mt-2">
                <ul className="nav common-tabs cms-tabs w-100" id="myTab" role="tablist">
                  {TABS.map((tab) => (
                    <li className="nav-item mr-3 my-2" key={tab.key}>
                      <a
                        className={`nav-link br-10 w-auto mx-0 text-center px-4 ${
                          cms.type === tab.key ? "active" : ""
                        }`}
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setCms({ type: tab.key });
                          setPage(1);
                          setSelectedRestaurantId(null);
                          setStartDate(null);
                          setEndDate(null);
                        }}
                      >
                        {tab.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <h6 className="my-4">REPORT HISTORY</h6>
            <div className="table-responsive px-0">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th width="80">S.No.</th>
                    <th>Name</th>
                    <th>Restaurant Name</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Generated On</th>
                    <th>Status</th>
                    <th className="text-center">Download</th>
                  </tr>
                </thead>
                <tbody>
                  {!canExport ? (
                    <tr>
                      <td colSpan="8" className="text-center py-4">
                        Coming soon
                      </td>
                    </tr>
                  ) : loadingHistory ? (
                    <tr>
                      <td colSpan="8" className="text-center py-4">
                        <div className="spinner-border" role="status">
                          <span className="sr-only">
                            {UI_CONSTANTS.BUTTON_LABELS.LOADING}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ) : historyRows.length > 0 ? (
                    historyRows.map((item, index) => {
                      const row = mapHistoryRow(item, index);
                      return (
                        <tr key={row.id}>
                          <td className="text-left">{row.serial}</td>
                          <td>{row.name}</td>
                          <td>{row.restaurantName}</td>
                          <td>{row.from}</td>
                          <td>{row.to}</td>
                          <td>{row.generatedOn}</td>
                          <td>
                            <span className={row.statusMeta.className}>
                              {row.statusMeta.label}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex align-items-center justify-content-center">
                              {row.isReady ? (
                                <a
                                  className="mx-2"
                                  href={row.downloadUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  title="Download"
                                >
                                  <img src={Download} alt="download" />
                                </a>
                              ) : row.status === REPORT_STATUS.FAILED ? (
                                <span className="text-danger small">Failed</span>
                              ) : (
                                <span className="text-warning small">Pending</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center py-4">
                        {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {canExport ? (
              <TablePagination page={page} totalPages={totalPages} onPageChange={setPage} />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SalesAndPerformanceReport;
